import { useEffect, useState, useCallback } from 'react';

export interface GPSPosition {
  latitude: number;
  longitude: number;
  accuracy_mm: number;       // Genauigkeit in Millimetern
  satellites?: number;
  hdop?: number;
  quality: 'rtk_fix' | 'rtk_float' | 'gps' | 'handy';
  recorded_at: string;
}

export interface GPSStatus {
  position: GPSPosition | null;
  fixType: 'rtk_fix' | 'rtk_float' | 'gps' | 'handy' | 'none';
  source: 'stab' | 'handy' | 'none';
  error: string | null;
  watching: boolean;
}

/**
 * Phase 1: Nur Handy-GPS via Geolocation API.
 * Phase 2: Hier kommt die Stab-Anbindung (Web Bluetooth + NMEA-Parser).
 */
export function useGPS(): GPSStatus & {
  start: () => void;
  stop: () => void;
} {
  const [position, setPosition] = useState<GPSPosition | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [watching, setWatching] = useState(false);
  const [watchId, setWatchId] = useState<number | null>(null);

  const start = useCallback(() => {
    if (!navigator.geolocation) {
      setError('Geolocation wird nicht unterstützt');
      return;
    }
    setError(null);
    setWatching(true);
    const id = navigator.geolocation.watchPosition(
      (pos) => {
        setPosition({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy_mm: Math.round(pos.coords.accuracy * 1000),  // Meter → mm
          quality: 'handy',
          recorded_at: new Date().toISOString(),
        });
        setError(null);
      },
      (err) => {
        setError(err.message);
      },
      {
        enableHighAccuracy: true,
        maximumAge: 1000,
        timeout: 10_000,
      }
    );
    setWatchId(id);
  }, []);

  const stop = useCallback(() => {
    if (watchId !== null) {
      navigator.geolocation.clearWatch(watchId);
      setWatchId(null);
    }
    setWatching(false);
  }, [watchId]);

  useEffect(() => {
    return () => { if (watchId !== null) navigator.geolocation.clearWatch(watchId); };
  }, [watchId]);

  return {
    position,
    fixType: position ? position.quality : 'none',
    source: position ? 'handy' : 'none',
    error,
    watching,
    start,
    stop,
  };
}
